<script>
  import { format, locale, locales } from 'svelte-i18n';
  import { Breadcrumb, BreadcrumbItem, Col, Container, Row, TabContent, TabPane } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';
  import { setDefaultOptions } from 'date-fns';
  import * as fnsLocales from 'date-fns/locale';

  import { Capability } from '@client/components/capability';
  import { DisplaySizeField } from '@client/components/fields/standalone';
  import { SelectField, ThemesField } from '@client/components/fields/standalone';
  import { SwitchField, TextField } from '@client/components/fields/standalone';
  import { Loader } from '@client/components/loader';
  import { MySignature } from '@client/components/my-signature';
  import { getNotificationsContext } from '@client/components/notifications';
  import { setTheme } from '@client/components/theme';
  import { UserFeatures } from '@client/components/user-features';
  import { UserNotifications } from '@client/components/user-notifications';

  import { disabledValue, enabledValue } from '@client/pages/manage/domain-settings';

  import { SettingsService } from '@client/services/settings';
  import { TagBlocksService } from '@client/services/tag-blocks';
  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';
  import { appSettingsStore } from '@client/stores/page-settings';
  import { hasCapability, userStore } from '@client/stores/user';

  import { DISPLAY_SIZES, LOCALES } from '@client/enums';

  import { merge } from '@common/utils/objects';
  import { isValidDomain, isValidEmail, isValidUrl } from '@common/utils/validation';

  const { addNotification } = getNotificationsContext();

  let data = {};
  let selectedLang = null;
  let hasMySignatureEnabled = false;
  let parameters = [];
  let paramsObject = {};
  let sigblock = {};

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const notify = (text) => {
    addNotification({
      text,
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onThemeUpdate = (id, label, item) => {
    setTheme(item.value);

    notify(
      $format('label.THEME_CHANGED', {
        values: {
          theme: item.label
        }
      })
    );
  };

  const onDisplayUpdate = (item) => {
    const root = document.documentElement;
    const app = document.getElementById('app');

    root.style.setProperty('--app-height', item.height);
    root.style.setProperty('--app-zoom', item.zoom);
    root.classList.remove(...DISPLAY_SIZES.map((i) => i.class));
    root.classList.add(item.class);

    app.style.zoom = item.zoom;

    $appSettingsStore.displaySize = item.value;

    notify(
      $format('label.DISPLAY_SIZE_CHANGED', {
        values: {
          size: $format(item.label)
        }
      })
    );
  };

  const onLocaleChange = (item) => {
    const language = LOCALES[item.value];

    locale.set(item.value);

    setDefaultOptions({
      locale: fnsLocales[item.value]
    });

    notify(
      $format('label.LANGUAGE_CHANGED', {
        values: {
          language: $format(item.label)
        }
      })
    );
  };

  const query = createQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const usermeta = await UsersService.getUserById($userStore.currentUser.email);
      const fields = usermeta.params.map(({ name }) => name);

      if (hasMySignatureEnabled && usermeta.sigcompliance) {
        sigblock = await TagBlocksService.getById(usermeta.sigcompliance.block);

        parameters = sigblock.params
          .map((config) => {
            const match = fields.includes(config.name);

            if (match) {
              return {
                name: config.name,
                default: usermeta.params.find(({ name }) => name === config.name)?.default || config.default
              };
            }

            return {
              name: config.name,
              default: config.default
            };
          })
          .filter(Boolean);

        paramsObject = parameters.reduce((acc, { name, default: d }) => {
          acc[name] = d;
          return acc;
        }, {});
      }

      return usermeta;
    }
  });

  const onUpdate = async (id, label, value) => {
    const [parent, child] = id.split('-');

    if (parent && child) {
      data[parent][child] = value;
    } else {
      data.user[id] = value;
    }

    let success = false;

    if (parent === 'usersettings') {
      success = await SettingsService.saveUserSettings({ [child]: value });

      if (success) {
        userStore.update((user) => {
          return {
            ...user,
            settings: {
              ...user.settings,
              [child]: value
            }
          };
        });
      }
    }

    if (success) {
      notify($format('label.SUCCESS.UPDATE', { values: { item: label } }));
    }
  };

  $: hasMySignatureEnabled = hasCapability('feature:enablemysignature');
  $: data = $query.data || {};
  $: paramsObject = parameters.reduce((acc, { name, default: value }) => {
    acc[name] = value;
    return acc;
  }, {});
  $: settings = merge(data?.usersettings || {});
  $: langItems = $locales.map((value) => ({ label: LOCALES[value] || value, value }));
  $: selectedLang = langItems.find((item) => item.value === $locale);
</script>

<div class="page settings">
  <Loader backdrop show={$query.isLoading || $query.isFetching} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem active>{$format('label.MY_SETTINGS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row>
        <Col>
          <h1 class="page-title">{$format('label.SETTINGS')}</h1>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <Container fluid class="pt-2 pb-5">
      <Row>
        <Col>
          <div style="max-width: 748px;">
            <TabContent>
              <TabPane tabId="general-tab" tab={$format('label.GENERAL')} active>
                <div class="fields">
                  <h2 class="heading">{$format('label.PREFERENCES')}</h2>
                  <div class="content">
                    <div class="field">
                      <SelectField
                        id="locale"
                        label={$format('label.LANGUAGE')}
                        tooltip={$format('label.TOOLTIP.LANGUAGE')}
                        selected={selectedLang}
                        options={langItems}
                        editing={true}
                        showActions={false}
                        onChange={onLocaleChange}
                      />
                    </div>

                    <div class="field">
                      <ThemesField {onThemeUpdate} />
                    </div>

                    {#if !isSafari && !$appStore.mobile}
                      <div class="field">
                        <DisplaySizeField {onDisplayUpdate} />
                      </div>
                    {/if}
                  </div>
                </div>

                <div class="fields">
                  <h2 class="heading">{$format('label.FEATURES')}</h2>
                  <div class="content">
                    <UserFeatures bind:settings {onUpdate} hideSalesEnabled />
                  </div>
                </div>

                <div class="fields">
                  <h2 class="heading">{$format('label.NOTIFICATIONS')}</h2>
                  <div class="content">
                    <UserNotifications bind:settings {onUpdate} expandAll />
                  </div>
                </div>
              </TabPane>
              <Capability action="feature:enablemysignature">
                <TabPane tabId="my-signature-tab" tab={$format('label.MY_SIGNATURE')}>
                  <div class="fields">
                    <h2 class="heading">{$format('label.PREVIEW')}</h2>
                    {#if parameters.length}
                      <div class="framed">
                        <div class="field heading columns">
                          <div class="name">{$format('label.FIELD')}</div>
                          <div class="value">{$format('label.VALUE')}</div>
                        </div>
                        {#each parameters as field}
                          <div class="field columns">
                            <div class="name">{field.name}</div>
                            {#if (data.params || []).find(({ name }) => name === field.name)?.default?.length}
                              <div class="value">{field.default}</div>
                            {:else}
                              <input
                                type="text"
                                class="value"
                                placeholder={$format('label.VALUE')}
                                bind:value={field.default}
                              />
                            {/if}
                          </div>
                        {/each}
                      </div>
                      <br />
                    {/if}
                    <MySignature email={$userStore.currentUser.email} bind:parameters={paramsObject} />
                  </div>
                </TabPane>
              </Capability>
            </TabContent>
          </div>
        </Col>
      </Row>
    </Container>
  </div>
</div>

<style lang="scss" src="./settings.scss"></style>
